import { AppBar, Badge, Box, Button, IconButton, Toolbar, Typography } from "@mui/material";
import { FilterAlt } from "@mui/icons-material";
import JamezzLogo from "../../assets/images/JamezzLogo.svg";
import React, { useMemo } from "react";
import { useDialog } from "../../global/utils/dialog/DialogProvider";
import SlideUpTransition from "./../../global/components/SlideUpTransition";
import useMenuTagFilters from "../../global/utils/menu/filters/useMenuTagFilters";
import Image from "../../global/components/Image";
import { useLocation } from "react-router";
import { createPath, routes } from "../../global/utils/config";
import { useAppSelector } from "../../global/utils/redux/store";
import useIsWidescreen from "../../global/utils/useIsWidescreen";
import isQr from "../../global/utils/helpers/isQr";
import { postAnalyticsEvent } from "../../global/utils/analytics/useAnalytics.ts";
import MenuFilterDialogV2 from "../pages/MenuTagFilterPage/MenuFilterDialogV2.tsx";
import MenuIcon from "@mui/icons-material/Menu";
import EmojiPeopleIcon from "@mui/icons-material/EmojiPeople";
import FormattedMessageJamezz from "../../global/components/FormattedMessageJamezz.tsx";
import useIsOrderingAllowed from "../../global/utils/hooks/useIsOrderingAllowed.ts";
import useServiceArticles from "../../global/components/useServiceArticles.tsx";
import SideMenuDrawer from "./SideMenuDrawer/SideMenuDrawer.tsx";
import ServiceBottomDrawer from "./ServiceBottomDrawer/ServiceBottomDrawer.tsx";

function TopBar() {
  const { openDialog } = useDialog();
  const { pathname } = useLocation();
  const isWidescreen = useIsWidescreen();

  const [openSideDrawer, setOpenSideDrawer] = React.useState(false);
  const [openServiceDrawer, setOpenServiceDrawer] = React.useState(false);

  const background = useAppSelector(
    (state) =>
      state.global.salesarea?.media_urls?.extra_settings__qrv5_topbar_background?.[0]?.conversions?.optimized?.url
  );
  const logoTopBar = useAppSelector(
    (state) => state.global.salesarea?.media_urls?.extra_settings__qrv5_logo_top_bar?.[0]?.conversions?.optimized?.url
  );
  const menuTagPreferences = useAppSelector((state) => state.menuState.menuTagPreferences);
  const isCatalog = useAppSelector((state) => state.global.salesarea.salesarea_type === "CATALOG");

  const menuTags = useMenuTagFilters();
  const serviceArticles = useServiceArticles();
  const canOrder = useIsOrderingAllowed();

  const isOnMenuPage = useMemo(() => pathname === createPath(routes.qr.rootPath), [pathname]);
  const isOnReturnPage = useMemo(() => pathname === createPath(routes.qr.returnPath), [pathname]);

  const numberOfPreferencesActive = useMemo(() => {
    return Object.values(menuTagPreferences).reduce((count, preference) => count + (preference.checked ? 1 : 0), 0);
  }, [menuTagPreferences]);

  const brandedIframeDesign = useAppSelector((state) => state.global.salesarea?.custom_data?.use_branded_iframe_design);

  if (isOnReturnPage) return null;

  return (
    <AppBar
      position={"sticky"}
      className={"JS-TopBar-AppBar"}
      sx={{
        width: !isCatalog && isWidescreen ? "calc(100vw - 450px)" : undefined,
        background: brandedIframeDesign ? "transparent" : background ? `url(${background})` : undefined,
        boxShadow: brandedIframeDesign ? "none" : undefined,
      }}
    >
      <SideMenuDrawer open={openSideDrawer} setOpen={setOpenSideDrawer} />
      <ServiceBottomDrawer open={openServiceDrawer} setOpen={setOpenServiceDrawer} />

      <Toolbar sx={{ height: 56 }}>
        <Box sx={{ position: "absolute", left: 16 }}>
          {/*<LanguageButton />*/}
          <IconButton
            data-cy={"side-drawer-menu-button"}
            size={"large"}
            edge={"start"}
            onClick={() => setOpenSideDrawer(true)}
          >
            <MenuIcon className={"JS-TopBar-MenuIcon"} />
          </IconButton>
        </Box>
        <Box
          sx={{
            height: 1,
            flex: "1",
            textAlign: "center",
            padding: 1,
            visibility: brandedIframeDesign ? "collapse" : undefined,
          }}
        >
          <Image
            key={logoTopBar ?? "jamezzlogo"}
            src={logoTopBar ?? JamezzLogo}
            className={"JS-TopBar-LogoImage"}
            style={{ height: "100%" }}
          />
        </Box>

        {serviceArticles.length && canOrder ? (
          <Button
            data-cy={"open-service-drawer-button"}
            onClick={() => setOpenServiceDrawer(true)}
            sx={{
              position: "absolute",
              right: 16,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              cursor: "pointer",
              minWidth: "unset",
            }}
          >
            <EmojiPeopleIcon sx={{ color: "text.primary" }} className={"JS-TopBar-ServiceIcon"} />
            <Typography
              className={"JS-TopBar-Service-Text"}
              sx={{
                fontSize: "0.55rem",
                color: "text.primary",
              }}
            >
              <FormattedMessageJamezz id={"Service"} />
            </Typography>
          </Button>
        ) : null}

        {serviceArticles.length === 0 && isOnMenuPage && menuTags.length > 0 ? (
          <IconButton
            sx={{ position: "absolute", right: 16 }}
            size={"large"}
            edge={"end"}
            onClick={() => {
              postAnalyticsEvent({
                category: "TopBar",
                action: "MenuFilterDialog",
              });
              openDialog({
                children: <MenuFilterDialogV2 />,
                fullScreen: !(isQr() && isWidescreen),
                TransitionComponent: SlideUpTransition,
              });
            }}
          >
            {numberOfPreferencesActive > 0 ? (
              <Badge badgeContent={numberOfPreferencesActive} color={"primary"}>
                <FilterAlt className={"JS-TopBar-AllergyIcon"} />
              </Badge>
            ) : (
              <FilterAlt className={"JS-TopBar-AllergyIcon"} />
            )}
          </IconButton>
        ) : null}
      </Toolbar>
    </AppBar>
  );
}

export default TopBar;
